import classnames from 'classnames';
import phone from 'phone';
import React, { useEffect } from 'react';

import {
  GenericInputGroup,
  GenericInputGroupProps,
  GenericInputProps,
  HUIAlert,
  HUIModal,
} from 'root/widgets/common-components';
import { INPUT_TYPES } from 'root/widgets/common-components/generic-input-group/constants';

import { CountryCode, HIVE_STATIC_VERSION } from 'root/widgets/constants';
import { EVENT_NAMES, emit } from 'root/widgets/events';
import { extractDataAutomationId, filterMetadata } from 'root/widgets/utils';

import './mobile-number-verification-modal.scss';
import { MobileNumberVerificationModalProps } from './types';

const MobileNumberVerificationModal: React.FC<MobileNumberVerificationModalProps> = ({
  metadata,
  context,
  className,
  config,
  body,
  input,
  error,
}) => {
  const defaultImage = `https://cdn.pgimgs.com/hive-ui/static/${HIVE_STATIC_VERSION}/images/verify-mobile-number.svg`;

  const { onClose, shouldShow } = config;
  const { title, description, mobileNumberLabel, image, button } = body;
  const { onSubmit, ...buttonProps } = button;
  const { region, invalidInputText, ...inputProps } = input;

  const mobileInputProps: GenericInputProps = {
    prefix: `+${CountryCode[region]}`,
    type: INPUT_TYPES.NUMBER,
    checkValidity: (text: string) => ({
      isInvalid: !phone(`${mobileInputProps.prefix}${text}`).isValid,
      error: invalidInputText,
    }),
    ...inputProps,
  };

  const handleSubmit = (inputValue: string) => {
    onSubmit(inputValue);

    emit(EVENT_NAMES.MOBILE_NUMBER_VERIFICATION.SUBMIT_ON_CLICK, context);
  };

  const inputGroup: GenericInputGroupProps = {
    input: mobileInputProps,
    inputError: error?.input,
    button: buttonProps,
    onSubmit: handleSubmit,
  };

  useEffect(() => {
    if (shouldShow) {
      emit(EVENT_NAMES.MOBILE_NUMBER_VERIFICATION.VIEW, context);
    }
  }, [shouldShow]);

  return (
    <HUIModal
      className={classnames('mobile-number-verification-root', className)}
      {...filterMetadata(metadata)}
      {...extractDataAutomationId(metadata)}
      show={shouldShow}
      onHide={onClose}
    >
      <HUIModal.Header className={classnames('mobile-number-verification-header')} closeButton />
      <HUIModal.Body className={classnames('mobile-number-verification-body', 'text-center')}>
        <img className="mobile-number-verification-image" src={image.src || defaultImage} alt={image.alt || title} />
        <h3 className="mobile-number-verification-title">{title}</h3>
        <p className="mobile-number-verification-description">{description}</p>
        <span className="verify-mobile-input-title">{mobileNumberLabel}</span>
        <GenericInputGroup {...inputGroup} />
        {error?.server && <HUIAlert variant="danger">{error?.server}</HUIAlert>}
      </HUIModal.Body>
    </HUIModal>
  );
};

MobileNumberVerificationModal.displayName = 'MobileNumberVerificationModal';

export default MobileNumberVerificationModal;
